import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { WizardLayout } from "@/components/we360/WizardLayout";
import DemoFormOverlay from "@/components/DemoFormOverlay";
import { useFormValidation } from "@/hooks/useFormValidation";
import { CheckCircle } from "lucide-react";
import { syncF3 } from "@/services/backendSync";
import Step1Welcome from "./Step1Welcome";
import Step2Vigorous from "./Step2Vigorous";
import Step3Moderate from "./Step3Moderate";
import Step4Walking from "./Step4Walking";
import Step5Results from "./Step5Results";
import Step6Summary from "./Step6Summary";
import { calculateMET, classifyIPAQ, getSittingCategory } from "./helpers";

const STEPS = [
  { id: "welcome", title: "Actividad Física", sub: "Evaluación IPAQ" },
  { id: "vigorous", title: "Actividad Vigorosa", sub: "Intensidad alta" },
  { id: "moderate", title: "Actividad Moderada", sub: "Intensidad media" },
  { id: "walking", title: "Caminata", sub: "Actividad base" },
  { id: "results", title: "Resultados", sub: "Sedentarismo + Clasificación" },
  { id: "summary", title: "Confirmación", sub: "Resumen final" },
];

function getStepRules(step, f3Data) {
  switch (step) {
    case 0: return {}; // Welcome - no validation
    case 1: return {
      vigorosaDias: { required: true, message: "Selecciona los días" },
      vigorosaTiempo: {
        required: false,
        custom: () => {
          if (f3Data.vigorosaDias && f3Data.vigorosaDias !== "0" && !f3Data.vigorosaTiempo)
            return "Selecciona el tiempo promedio";
          return null;
        },
      },
    };
    case 2: return {
      moderadaDias: { required: true, message: "Selecciona los días" },
      moderadaTiempo: {
        required: false,
        custom: () => {
          if (f3Data.moderadaDias && f3Data.moderadaDias !== "0" && !f3Data.moderadaTiempo)
            return "Selecciona el tiempo promedio";
          return null;
        },
      },
    };
    case 3: return {
      caminataDias: { required: true, message: "Selecciona los días" },
      caminataTiempo: {
        required: false,
        custom: () => {
          if (f3Data.caminataDias && f3Data.caminataDias !== "0" && !f3Data.caminataTiempo)
            return "Selecciona el tiempo promedio";
          return null;
        },
      },
    };
    case 4: return {
      tiempoSentado: { required: true, message: "Selecciona las horas" },
    };
    case 5: return {}; // Summary - no validation
    default: return {};
  }
}

export default function FormF3() {
  const { profile, setProfile, isDemo } = useUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [fade, setFade] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { validateStep, getError, isStepValid, clearErrors, onFieldChange } = useFormValidation();

  const [f3Data, setF3Data] = useState({
    vigorosaDias: "", vigorosaTiempo: "",
    moderadaDias: "", moderadaTiempo: "",
    caminataDias: "", caminataTiempo: "",
    tiempoSentado: "",
  });

  const rules = useMemo(() => getStepRules(step, f3Data), [step, f3Data]);

  const setF3Field = useCallback((key, value) => {
    setF3Data((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "vigorosaDias" && value === "0") next.vigorosaTiempo = "";
      if (key === "moderadaDias" && value === "0") next.moderadaTiempo = "";
      if (key === "caminataDias" && value === "0") next.caminataTiempo = "";
      return next;
    });
    // Clear validation errors in real-time
    const rule = rules[key];
    if (rule) onFieldChange(key, value, rule);
  }, [rules, onFieldChange]);

  const metVig = calculateMET(8.0, f3Data.vigorosaDias, f3Data.vigorosaTiempo);
  const metMod = calculateMET(4.0, f3Data.moderadaDias, f3Data.moderadaTiempo);
  const metWalk = calculateMET(3.3, f3Data.caminataDias, f3Data.caminataTiempo);
  const ipaqClass = classifyIPAQ(metVig, metMod, metWalk);
  const sittingCat = getSittingCategory(f3Data.tiempoSentado);

  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => { setStep((s) => s + dir); setFade(true); }, 200);
  }, []);

  const goNext = useCallback(() => {
    if (isDemo) { go(1); return; }
    const errs = validateStep(rules, f3Data);
    if (Object.keys(errs).length > 0) return;
    go(1);
  }, [step, rules, f3Data, validateStep, go, isDemo]);

  const goBack = useCallback(() => { clearErrors(); go(-1); }, [go, clearErrors]);

  const nextDisabled = !isDemo && !isStepValid(rules, f3Data);

  const handleSubmit = useCallback(async () => {
    if (isDemo) return;
    const now = new Date().toISOString();
    const f3Results = {
      vigorosa: { dias: parseInt(f3Data.vigorosaDias) || 0, tiempo: f3Data.vigorosaTiempo, metMin: metVig || 0 },
      moderada: { dias: parseInt(f3Data.moderadaDias) || 0, tiempo: f3Data.moderadaTiempo, metMin: metMod || 0 },
      caminata: { dias: parseInt(f3Data.caminataDias) || 0, tiempo: f3Data.caminataTiempo, metMin: metWalk || 0 },
      metTotal: ipaqClass.metTotal,
      clasificacionIPAQ: ipaqClass.key,
      tiempoSentado: f3Data.tiempoSentado,
      indiceSedentarismo: sittingCat?.key || "unknown",
      createdAt: now,
      completedAt: now,
    };
    setSubmitted(true);
    await setProfile((prev) => ({
      ...prev,
      f3Results,
      fhirSyncStatus: "PENDING_FHIR",
      pendingFhirF3: true,
    }));

    const email = user?.email || profile.email;
    if (email) {
      syncF3(f3Results, email).then((res) => {
        if (res.ok) {
          console.log("[F3] Backend sync OK:", res.data);
          setProfile((prev) => ({
            ...prev,
            pendingFhirF3: false,
            fhirSyncStatus: "SYNCED",
            fhirSyncedAt: new Date().toISOString(),
          }));
        } else {
          console.warn("[F3] Backend sync failed (queued in Outbox):", res);
        }
      });
    }

    setTimeout(() => navigate("/dashboard"), 2000);
  }, [f3Data, metVig, metMod, metWalk, ipaqClass, sittingCat, setProfile, navigate, isDemo, user, profile.email]);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-8 text-center max-w-[380px] w-full" style={{ boxShadow: "0 8px 30px rgba(45,42,36,0.10)" }} data-testid="f3-success-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#E2F0E8" }}>
            <CheckCircle size={32} color="#157346" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D2A24" }}>¡Evaluación IPAQ completada!</h2>
          <p className="text-sm" style={{ color: "#71644C" }}>Redirigiendo a tu dashboard...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const errProps = { errors: { getError } };
    switch (step) {
      case 0: return <Step1Welcome userName={profile.nombres} />;
      case 1: return <Step2Vigorous dias={f3Data.vigorosaDias} onDiasChange={(v) => setF3Field("vigorosaDias", v)} tiempo={f3Data.vigorosaTiempo} onTiempoChange={(v) => setF3Field("vigorosaTiempo", v)} {...errProps} />;
      case 2: return <Step3Moderate dias={f3Data.moderadaDias} onDiasChange={(v) => setF3Field("moderadaDias", v)} tiempo={f3Data.moderadaTiempo} onTiempoChange={(v) => setF3Field("moderadaTiempo", v)} {...errProps} />;
      case 3: return <Step4Walking dias={f3Data.caminataDias} onDiasChange={(v) => setF3Field("caminataDias", v)} tiempo={f3Data.caminataTiempo} onTiempoChange={(v) => setF3Field("caminataTiempo", v)} {...errProps} />;
      case 4: return <Step5Results tiempoSentado={f3Data.tiempoSentado} onTiempoSentadoChange={(v) => setF3Field("tiempoSentado", v)} metVig={metVig} metMod={metMod} metWalk={metWalk} ipaqClass={ipaqClass} sittingCat={sittingCat} {...errProps} />;
      case 5: return <Step6Summary metVig={metVig} metMod={metMod} metWalk={metWalk} ipaqClass={ipaqClass} sittingCat={sittingCat} />;
      default: return null;
    }
  };

  return (
    <DemoFormOverlay>
      <WizardLayout
        steps={STEPS}
        currentStep={step}
        onNext={goNext}
        onBack={goBack}
        fade={fade}
        userName={profile.nombres}
        isLastStep={step === 5}
        onSubmit={handleSubmit}
        submitDisabled={isDemo}
        nextDisabled={nextDisabled}
        nextLabel={step === 0 ? "Comenzar evaluación \u2192" : undefined}
        submitLabel="Enviar evaluación"
      >
        {renderStep()}
      </WizardLayout>
    </DemoFormOverlay>
  );
}
